/**
 * Bus Loading Page - Bus Inpakken
 *
 * Date selector and route list for bus loading.
 * Shows only Routes__c where Locked__c = true.
 * Displays loading progress per route.
 * Items are sorted DESC (last stop first) so they go into the bus first.
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { WbButton, WbChip, WbDateStrip, WbListRow, WbSectionKop, WbState } from '../components/wb';
import { useHeader } from '../stores';
import { salesforceClient } from '../lib/salesforce';
import type { BusLoadingRoute } from '../lib/salesforce';

// Polling interval for real-time updates (30 seconds)
const POLLING_INTERVAL_MS = 30000;

export function BusLoadingPage() {
  const navigate = useNavigate();

  // Default to tomorrow
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const tomorrowStr = tomorrow.toISOString().split('T')[0]!;

  const [selectedDate, setSelectedDate] = useState<string>(tomorrowStr);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [routes, setRoutes] = useState<BusLoadingRoute[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Generate dates for today + 7 days
  const dates = useMemo(() => Array.from({ length: 8 }, (_, i) => {
    const date = new Date();
    date.setDate(date.getDate() + i);
    return {
      dateStr: date.toISOString().split('T')[0]!,
      dayName: new Intl.DateTimeFormat('nl-NL', { weekday: 'short' }).format(date),
      dayNum: date.getDate(),
      isToday: i === 0,
      isTomorrow: i === 1,
    };
  }), []);

  // Fetch routes for the selected date
  const fetchRoutes = useCallback(async (date: string, showRefreshSpinner = false) => {
    try {
      if (showRefreshSpinner) {
        setIsRefreshing(true);
      } else {
        setIsLoading(true);
      }
      setError(null);

      const response = await salesforceClient.getBusLoadingRoutes(date);
      console.log('[BusLoadingPage] API response:', response);

      if (response.success && response.data) {
        setRoutes(response.data.routes || []);
      } else {
        console.error('[BusLoadingPage] API error response:', response);
        setError(response.message || 'Kon routes niet ophalen');
        setRoutes([]);
      }
    } catch (err) {
      console.error('Error fetching bus loading routes:', err);
      setError(err instanceof Error ? err.message : 'Er ging iets mis bij het laden van routes');
      setRoutes([]);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  // Fetch routes when date changes
  useEffect(() => {
    fetchRoutes(selectedDate);
  }, [selectedDate, fetchRoutes]);

  // Polling for real-time updates
  useEffect(() => {
    const interval = setInterval(() => {
      fetchRoutes(selectedDate, true);
    }, POLLING_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [selectedDate, fetchRoutes]);

  // Pull to refresh handler
  const handleRefresh = async () => {
    await fetchRoutes(selectedDate, true);
  };

  const formatDateLabel = (dateStr: string) => {
    const date = new Date(dateStr);
    const today = new Date();
    const tomorrow = new Date();
    tomorrow.setDate(today.getDate() + 1);

    if (dateStr === today.toISOString().split('T')[0]) return 'Vandaag';
    if (dateStr === tomorrow.toISOString().split('T')[0]) return 'Morgen';

    return new Intl.DateTimeFormat('nl-NL', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    }).format(date);
  };

  // Calculate progress percentage
  const getProgressPercentage = (route: BusLoadingRoute) => {
    if (route.totalItems === 0) return 100;
    return Math.round((route.loadedItems / route.totalItems) * 100);
  };

  useHeader('Bus laden', formatDateLabel(selectedDate));

  return (
    <div className="space-y-4">
      <WbDateStrip
        dagen={dates.map((d) => ({ dateStr: d.dateStr, label: d.isToday ? 'Vandaag' : d.isTomorrow ? 'Morgen' : d.dayName, dayNum: d.dayNum, isToday: d.isToday }))}
        value={selectedDate}
        onChange={setSelectedDate}
        scrollNaar={tomorrowStr}
      />

      {isLoading && <WbState variant="laden" titel="Routes laden…" />}
      {!isLoading && error && <WbState variant="fout" titel={error} onOpnieuw={() => fetchRoutes(selectedDate)} />}
      {!isLoading && !error && routes.length === 0 && (
        <WbState variant="leeg" titel="Geen routes om te laden." tekst="Er zijn geen vergrendelde routes voor deze datum." />
      )}

      {!isLoading && !error && routes.length > 0 && (
        <section className="space-y-1.5">
          <WbSectionKop
            label="Routes"
            teller={routes.length}
            actie={
              <WbButton variant="link" onClick={handleRefresh} disabled={isRefreshing}>
                {isRefreshing ? 'Verversen…' : 'Ververs'}
              </WbButton>
            }
          />
          {routes.map((route) => {
            const progressPct = getProgressPercentage(route);
            const isComplete = route.isFullyLoaded;
            return (
              <WbListRow
                key={route.routeId}
                onClick={() => navigate(`/bus-loading/${route.routeId}`)}
                tekst
                hoofd={route.routeName}
                klaar={isComplete}
                chips={isComplete ? <WbChip kind="klaar">Geladen</WbChip> : <WbChip kind="neutraal">{progressPct}%</WbChip>}
                sub={`${route.loadedItems}/${route.totalItems} items${route.driverName ? ` · ${route.driverName}` : ''}`}
              />
            );
          })}
        </section>
      )}

      <p className="text-sm text-wb-inkt2">Items staan in omgekeerde volgorde: de laatste stop gaat als eerste de bus in.</p>
    </div>
  );
}
